import { useApolloClient } from '@apollo/client'
import { usePlaylistActions } from 'app/_hooks/usePlaylistActions'
import { useSnackbar } from 'app/_hooks/useSnackbar'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { GET_PLAYLISTS } from '~/features/play_list/graphql/query/getPlaylistRegistration'
import { PlayListFormValues, usePlayListActionApi } from './usePlayListActionApi'
import { usePlayListRegistrationInfo } from './usePlayListRegistrationInfo'
import { useAddPlaylistVideo } from './useAddPlaylistVideo'
import { useRemovePlaylistVideo } from './useRemovePlaylistVideo'

export function usePlayListVideoActionController() {
  const { t } = useTranslation()
  const showSnackbar = useSnackbar()
  const client = useApolloClient()
  const { setPlayListVideoActionUiType } = usePlaylistActions()
  const { addPlaylistVideo } = useAddPlaylistVideo()
  const { createPlayListApi } = usePlayListActionApi()
  const { removePlaylistVideo } = useRemovePlaylistVideo()
  const playListInfoForView = usePlayListRegistrationInfo()

  const onClose = useCallback(() => {
    setPlayListVideoActionUiType('standby')
  }, [setPlayListVideoActionUiType])

  const onAddVideoToPlayList = useCallback(
    async (targetPlayListId: number, videoId: number, onFinish: () => void) => {
      try {
        await addPlaylistVideo(
          String(targetPlayListId),
          String(videoId),
        )
      } catch (error) {
        // TODO: show error snackbar once localization strings are ready
        console.error('Failed to add video to playlist:', error)
      } finally {
        onFinish()
      }
    },
    [addPlaylistVideo],
  )

  const onDeleteVideoFromPlayList = useCallback(
    async (targetPlayListId: string, videoId: string, onFinish: () => void) => {
      try {
        await removePlaylistVideo(targetPlayListId, videoId)
      } catch (error) {
        console.error('Failed to delete video from playlist:', error)
        showSnackbar(t('failed_to_delete_video_from_playlist'))
      } finally {
        onFinish()
      }
    },
    [removePlaylistVideo, showSnackbar, t],
  )

  const onCreatePlayListWithAddVideo = useCallback(
    async (values: PlayListFormValues, videoId: number, onFinish: () => void) => {
      try {
        const res = await createPlayListApi(values)
        const newId = String(res.id)
        const vId = String(videoId)

        const existing = client.readQuery({ query: GET_PLAYLISTS })
        if (existing) {
          client.writeQuery({
            query: GET_PLAYLISTS,
            data: {
              ...existing,
              playlists: [
                ...existing.playlists,
                { __typename: 'Playlist' as const, id: newId, title: values.title },
              ],
            },
          })
        }

        await addPlaylistVideo(newId, vId)
      } catch (error) {
        console.error('Failed to create playlist with video:', error)
        showSnackbar(t('failed_to_create_playlist'))
      } finally {
        onFinish()
        setPlayListVideoActionUiType('standby')
      }
    },
    [createPlayListApi, addPlaylistVideo, setPlayListVideoActionUiType, client, showSnackbar, t],
  )

  const startDeletePlayListVideo = useCallback(
    async (targetPlayListId: string, videoId: string, onFinish: () => void) => {
      try {
        await removePlaylistVideo(targetPlayListId, videoId)
      } catch (error) {
        console.error('Failed to delete video from playlist:', error)
        showSnackbar(t('failed_to_delete_video_from_playlist'))
      } finally {
        onFinish()
        setPlayListVideoActionUiType('standby')
      }
    },
    [removePlaylistVideo, setPlayListVideoActionUiType, showSnackbar, t],
  )

  const startNewPlayList = useCallback(() => {
    setPlayListVideoActionUiType('createPlayList')
  }, [setPlayListVideoActionUiType])

  const startAddVideoToPlayList = useCallback(() => {
    setPlayListVideoActionUiType('select')
  }, [setPlayListVideoActionUiType])

  return useMemo(
    () => ({
      onClose,
      onAddVideoToPlayList,
      onDeleteVideoFromPlayList,
      onCreatePlayListWithAddVideo,
      startNewPlayList,
      startAddVideoToPlayList,
      startDeletePlayListVideo,
      playListInfoForView,
    }),
    [
      onClose,
      onAddVideoToPlayList,
      onDeleteVideoFromPlayList,
      onCreatePlayListWithAddVideo,
      startNewPlayList,
      startAddVideoToPlayList,
      startDeletePlayListVideo,
      playListInfoForView,
    ],
  )
}
